import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Copy, Loader2, Pause, Play, Share2, Users, Volume2, VolumeX, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/auth";
import { playBell, chimeOn } from "@/lib/chime";
import { playVoice, stopVoice } from "@/lib/ariel";
import { localDay, useQuantumJourney, vibrate } from "@/lib/quantum";
import { Page } from "@/components/app/AppKit";
import BreathCircle from "@/components/quantum/BreathCircle";
import { Ring, StepCard } from "@/components/quantum/GuidedPractice";
import PartnerMessages from "@/components/quantum/PartnerMessages";
import LoveNote from "@/components/quantum/LoveNote";

const btn = "inline-flex items-center justify-center gap-2 rounded-full py-3.5 px-5 font-tt-mono text-[12px] uppercase tracking-[0.2em] transition-colors disabled:opacity-50";
const gold = `${btn} bg-[#D9B47A] text-[#0A0706] hover:bg-[#F2E8DF]`;
const ghost = `${btn} border border-[#D9B47A]/40 text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706]`;
const field = "w-full bg-[#0F070B] border border-[#D9B47A]/25 px-4 py-3 text-[16px] text-[#F2E8DF] focus:outline-none focus:border-[#E8838E]";

const useGatePractices = (g) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (g === "") return;
    api.get(`/quantum/gates/${g}`).then(({ data }) => setList(data?.segments?.practices ?? [])).catch(() => setList([]));
  }, [g]);
  return list;
};

// /quantum/partner — start a shared session or join one with a code.
export function PartnerLobby() {
  const navigate = useNavigate();
  const { gates } = useQuantumJourney();
  const open = gates.filter((x) => x.published && !x.locked && x.practices);
  const [g, setG] = useState("");
  const [p, setP] = useState(0);
  const [code, setCode] = useState(new URLSearchParams(window.location.search).get("code") ?? "");
  const [busy, setBusy] = useState("");
  const practices = useGatePractices(g);
  useEffect(() => {
    if (g === "" && open.length) setG(String(open[0].g));
  }, [g, open]);
  const start = async () => {
    setBusy("start");
    try {
      const { data } = await api.post("/quantum/partner", { g: Number(g), p: Number(p) });
      navigate(`/quantum/partner/${data.code}`);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
      setBusy("");
    }
  };
  const join = async (e) => {
    e.preventDefault();
    setBusy("join");
    try {
      const { data } = await api.post("/quantum/partner/join", { code });
      navigate(`/quantum/partner/${data.code}`);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
      setBusy("");
    }
  };
  return (
    <Page testId="qi-partner-lobby" eyebrow="Partner sessions" title={<>Practise <em className="text-[#E8838E]">in step.</em></>}>
      <p className="mt-4 text-[15px] text-[#CFC2B6] leading-relaxed">Two initiates move through the same practice together, step by step, on one shared timer, in the same room or across any distance. Your partner needs their own Quantum Intimacy access.</p>
      <section className="mt-10 border border-[#D9B47A]/20 p-6">
        <p className="font-display text-2xl">Begin a session</p>
        {open.length ? (
          <>
            <label className="block mt-5 text-[13px] text-[#A8998C]">Gate</label>
            <select data-testid="qi-partner-gate" value={g} onChange={(e) => { setG(e.target.value); setP(0); }} className={`${field} mt-2`}>
              {open.map((x) => <option key={x.g} value={x.g}>{`Gate ${x.g + 1} · ${x.name}`}</option>)}
            </select>
            <label className="block mt-4 text-[13px] text-[#A8998C]">Practice</label>
            <select data-testid="qi-partner-practice" value={p} onChange={(e) => setP(e.target.value)} className={`${field} mt-2`}>
              {practices.map((x, k) => <option key={k} value={k}>{`${x.mode === "partner" ? "Partner" : "Solo"} · ${x.title}`}</option>)}
            </select>
            <button data-testid="qi-partner-start" onClick={start} disabled={!!busy || !practices.length} className={`${gold} w-full mt-6`}>{busy === "start" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} Create session</button>
          </>
        ) : (
          <p data-testid="qi-partner-none" className="mt-3 text-[15px] text-[#A8998C]">Partner sessions open once your first gate with a practice is available.</p>
        )}
      </section>
      <form onSubmit={join} className="mt-6 border border-[#D9B47A]/20 p-6">
        <p className="font-display text-2xl">Join your partner</p>
        <input data-testid="qi-partner-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} placeholder="Session code" className={`${field} mt-5 font-tt-mono tracking-[0.3em] uppercase`} />
        <button data-testid="qi-partner-join" disabled={!!busy || code.trim().length < 4} className={`${ghost} w-full mt-4`}>{busy === "join" && <Loader2 className="w-4 h-4 animate-spin" />} Join session</button>
      </form>
    </Page>
  );
}

const Invite = ({ code }) => {
  const link = `${window.location.origin}/quantum/partner?code=${code}`;
  const share = async () => {
    if (navigator.share) await navigator.share({ title: "Quantum Intimacy", text: `Join me in Quantum Intimacy. Session code ${code}`, url: link }).catch(() => {});
    else {
      await navigator.clipboard?.writeText(link).catch(() => {});
      toast.success("Invitation link copied.");
    }
  };
  return (
    <div data-testid="qi-partner-invite" className="mt-6 border border-[#E8838E]/30 bg-[#140910] p-6">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#A8998C]">Session code</p>
      <p data-testid="qi-partner-session-code" className="mt-2 font-tt-mono text-4xl tracking-[0.35em] text-[#F2E8DF]">{code}</p>
      <div className="mt-5 flex gap-2">
        <button data-testid="qi-partner-share" onClick={share} className={`${ghost} flex-1`}><Share2 className="w-4 h-4" /> Invite</button>
        <button data-testid="qi-partner-copy" onClick={() => navigator.clipboard?.writeText(code).then(() => toast.success("Code copied.")).catch(() => {})} className={ghost} aria-label="Copy code"><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

// /quantum/partner/:code — the shared session, kept in step by polling the server clock.
export function PartnerSession() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [voice, setVoice] = useState(false);
  const offset = useRef(0);
  const rang = useRef("");
  const spoken = useRef("");
  const logged = useRef(false);
  const load = useCallback(() => api.get(`/quantum/partner/${code}`).then(({ data }) => {
    offset.current = new Date(data.server_now).getTime() - Date.now();
    setS(data);
  }).catch((err) => setError(errText(err.response?.data?.detail))), [code]);
  useEffect(() => {
    load();
    const t = setInterval(load, 1500);
    const tick = setInterval(() => setNow(Date.now()), 250);
    return () => {
      clearInterval(t);
      clearInterval(tick);
      stopVoice();
    };
  }, [load]);
  const step = s && s.step >= 0 ? s.practice.steps[s.step] : null;
  const left = !step ? 0 : s.running ? Math.max(0, Math.ceil((new Date(s.ends_at).getTime() - (now + offset.current)) / 1000)) : s.remaining ?? step.seconds;
  useEffect(() => {
    if (!step || !s.running || left > 0) return;
    const key = `${s.step}-${s.ends_at}`;
    if (rang.current === key) return;
    rang.current = key;
    if (chimeOn()) playBell();
    vibrate([60, 80, 60]);
  }, [left, step, s]);
  useEffect(() => {
    if (!voice || !step || spoken.current === `${s.step}`) return;
    spoken.current = `${s.step}`;
    vibrate(40);
    playVoice(`${step.title}. ${step.text}`).catch(() => {});
  }, [voice, step, s]);
  useEffect(() => {
    if (!s?.ended || logged.current || s.step < s.practice.steps.length - 1) return;
    logged.current = true;
    api.post("/quantum/practice-log", { g: s.g, practice: s.practice.title, day: localDay(), partner: true }).catch(() => {});
  }, [s]);
  const control = async (action) => {
    try {
      await api.post(`/quantum/partner/${code}/control`, { action });
      await load();
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    }
  };
  if (error) return <Page testId="qi-partner-error" title="Session closed."><p className="mt-4 text-[#CFC2B6]">{error}</p><button onClick={() => navigate("/quantum/partner")} className={`${ghost} mt-6`}>Partner sessions</button></Page>;
  if (!s) return <Page testId="qi-partner-loading"><Loader2 className="mt-16 w-5 h-5 animate-spin text-[#D9B47A]" /></Page>;
  const other = s.is_host ? s.guest_name : s.host_name;
  const last = s.step === s.practice.steps.length - 1;
  if (s.ended) {
    return (
      <Page testId="qi-partner-ended" eyebrow="Partner session" title={<>Held <em className="text-[#E8838E]">together.</em></>}>
        <p className="mt-4 text-[#CFC2B6]">{s.step >= s.practice.steps.length - 1 ? "Your shared practice is complete and saved to your field." : "This session has closed."}</p>
        {s.guest_name && <div className="mt-8"><LoveNote code={s.code} partner={other} testId="qi-partner-love-note" /></div>}
        <button data-testid="qi-partner-again" onClick={() => navigate("/quantum/partner")} className={`${gold} mt-8 w-full`}>Another session</button>
      </Page>
    );
  }
  return (
    <Page testId="qi-partner-session" eyebrow={`Partner session · Gate ${s.g + 1} · ${s.gate}`} title={s.practice.title}>
      <div className="mt-5 flex items-center justify-between gap-3">
        <p data-testid="qi-partner-presence" className="flex items-center gap-2 text-[14px] text-[#CFC2B6]">
          <span className={`w-2.5 h-2.5 rounded-full ${s.guest_name ? "bg-[#7FCB8B] shadow-[0_0_10px_#7FCB8B]" : "bg-[#8F8176] animate-pulse"}`} />
          {s.guest_name ? `Practising with ${other}` : "Waiting for your partner to join…"}
        </p>
        <button data-testid="qi-partner-voice" onClick={() => setVoice((v) => !v)} aria-pressed={voice} className="inline-flex items-center gap-1.5 text-[12px] text-[#D9B47A]">{voice ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} Ariel {voice ? "on" : "off"}</button>
      </div>
      {s.is_host && !s.guest_name && <Invite code={s.code} />}
      {!step ? (
        <div data-testid="qi-partner-intro" className="mt-6 border border-[#D9B47A]/20 p-7">
          <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#E8838E]">{s.practice.steps.length} steps · either of you can lead</p>
          <p className="mt-4 text-[16px] leading-relaxed text-[#CFC2B6]">{s.practice.intro}</p>
          <button data-testid="qi-partner-begin" onClick={() => control("next")} className={`${gold} w-full mt-8`}>Begin together</button>
        </div>
      ) : (
        <StepCard step={step} k={s.step} total={s.practice.steps.length} partner>
          {step.seconds > 0 && (
            <>
              <div className="mt-8"><BreathCircle active={s.running && left > 0} /></div>
              <div className="mt-6 flex items-center gap-5">
                <Ring left={left} seconds={step.seconds} />
                <button data-testid="qi-partner-timer" onClick={() => control(s.running ? "pause" : "start")} disabled={!s.running && left === 0} className="inline-flex items-center gap-2 rounded-full border border-[#E8838E]/50 px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#E8838E] hover:bg-[#E8838E] hover:text-[#0A0706] transition-colors disabled:opacity-40">
                  {s.running ? <><Pause className="w-3.5 h-3.5" /> Pause for both</> : <><Play className="w-3.5 h-3.5" /> Start for both</>}
                </button>
              </div>
            </>
          )}
        </StepCard>
      )}
      {step && (
        <div className="mt-5 flex gap-3">
          <button data-testid="qi-partner-prev" onClick={() => control("prev")} className="w-12 h-12 rounded-full border border-[#D9B47A]/30 flex items-center justify-center text-[#D9B47A]" aria-label="Previous step"><ArrowLeft className="w-4 h-4" /></button>
          <button data-testid={last ? "qi-partner-complete" : "qi-partner-next"} onClick={() => control(last ? "end" : "next")} className={`${gold} flex-1`}>{last ? "Complete together" : <>Next step <ArrowRight className="w-4 h-4" /></>}</button>
        </div>
      )}
      <PartnerMessages code={s.code} messages={Array.isArray(s.messages) ? s.messages : []} joined={Boolean(s.guest_name)} partner={other} />
      <button data-testid="qi-partner-end" onClick={() => window.confirm("End this session for both of you?") && control("end")} className="mt-8 inline-flex items-center gap-2 text-sm text-[#A8998C] underline underline-offset-4"><X className="w-4 h-4" /> End session</button>
    </Page>
  );
}
